import type { textParticlesSkinParams } from '../TextParticles.skinParams';

export type Dimensions = {
  width: number;
  height: number;
};

export type Coordinates = {
  x: number;
  y: number;
};

export enum AnimationType {
  IN = 'in',
  OUT = 'out',
}

export type Particle = {
  x: number;
  y: number;
  initialX: number;
  initialY: number;
  targetX: number;
  targetY: number;
  radius: number;
  delay: number;
  scale: number;
  lifetime: number;
  startTime?: number;
  progress: number;
  sourceImageCoordinates: Coordinates;
};

export interface EffectParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  startTime: number;
  lifetime: number;
  radius: number;
  colorProgression: Array<string>;
  progress: number;
  particleStartSize: number;
  particleEndSize: number;
  particleStartOpacity: number;
  particleEndOpacity: number;
}

export type InstanceState = {
  particles: Array<Particle>;
  animationFrameId: number;
  frameCanvas: OffscreenCanvas;
  frameContext: OffscreenCanvasRenderingContext2D;
  mainCanvas: OffscreenCanvas;
  mainContext: ImageBitmapRenderingContext;
  imageData: ImageData;
  imageBitmap: ImageBitmap;
  effectConfiguration: EffectConfiguration;
  textBoundaries: TextBoundaries;
  animationProgress: number;
  animationState: AnimationState;
  absoluteElapsedTime: number;
  absoluteStartTime: number;
  devicePixelRatio: number;
  effectProps: {
    targetParticleCount: number;
    emittedBlocks: Set<string>;
    effectParticles: Array<EffectParticle>;
    preselectedEmittingBlocks: Set<string>;
    effectImageData: ImageData;
  };
};

export interface WindowWithWorker extends Window {
  particlesAnimationWorker?: Worker;
}

export const INITIALIZE = 'INITIALIZE' as const;
export const PLAY = 'PLAY' as const;
export const STOP = 'STOP' as const;
export const PAUSE = 'PAUSE' as const;
export const UPDATE_IMAGE = 'UPDATE_IMAGE' as const;
export const TERMINATE_INSTANCE = 'TERMINATE_INSTANCE' as const;
export const SET_PREVIEW = 'SET_PREVIEW' as const;
export const UPDATE_RENDERER_PROPS = 'UPDATE_RENDERER_PROPS' as const;

export const ANIMATION_FINISHED = 'ANIMATION_FINISHED' as const;
export const INITIALIZED = 'INITIALIZED' as const;
export const ANIMATION_STOPPED = 'ANIMATION_STOPPED' as const;

export type InitializeMessagePayload = {
  imageBitmap: ImageBitmap;
  canvas: OffscreenCanvas;
  effectConfiguration: EffectConfiguration;
  devicePixelRatio: number;
  targetParticleCount: number;
};

export type UpdateImageMessagePayload = {
  imageBitmap: ImageBitmap;
};

export type UpdateRendererPropsPayload = EffectConfiguration;

type MessageType =
  | typeof INITIALIZE
  | typeof PLAY
  | typeof STOP
  | typeof PAUSE
  | typeof UPDATE_IMAGE
  | typeof TERMINATE_INSTANCE
  | typeof ANIMATION_FINISHED
  | typeof INITIALIZED
  | typeof SET_PREVIEW
  | typeof UPDATE_RENDERER_PROPS
  | typeof ANIMATION_STOPPED;

export type MessagePayloadMap = {
  [INITIALIZE]: InitializeMessagePayload;
  [PLAY]: undefined;
  [STOP]: undefined;
  [PAUSE]: undefined;
  [UPDATE_IMAGE]: UpdateImageMessagePayload;
  [TERMINATE_INSTANCE]: undefined;
  [ANIMATION_FINISHED]: undefined;
  [INITIALIZED]: undefined;
  [SET_PREVIEW]: undefined;
  [UPDATE_RENDERER_PROPS]: UpdateRendererPropsPayload;
  [ANIMATION_STOPPED]: undefined;
};

export type Message<T extends MessageType> = {
  type: T;
  payload: MessagePayloadMap[T];
  componentId: string;
};

export type MainThreadMessage =
  | Message<typeof INITIALIZE>
  | Message<typeof PLAY>
  | Message<typeof STOP>
  | Message<typeof PAUSE>
  | Message<typeof UPDATE_IMAGE>
  | Message<typeof TERMINATE_INSTANCE>
  | Message<typeof SET_PREVIEW>
  | Message<typeof UPDATE_RENDERER_PROPS>;

export type WorkerMessageTypes =
  | typeof ANIMATION_FINISHED
  | typeof INITIALIZED
  | typeof ANIMATION_STOPPED;

export type WorkerMessage =
  | Message<typeof ANIMATION_FINISHED>
  | Message<typeof INITIALIZED>;

export type AnimationState =
  | 'playing'
  | 'stopped'
  | 'preview'
  | 'finished'
  | 'paused';

export type TextBoundaries = Dimensions & {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

export type StartPosition =
  | 'center'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'random';

export type CommonEffectConfiguration = {
  effectType: EffectType;
  text: string;
  animationDuration: number;
  presetId: string;
  color: string;
};

export type ParticleColorConfiguration = {
  particleColorCount: number;
  particleColor1: string;
  particleColor2: string;
  particleColor3: string;
  particleColor4: string;
};

export type ParticleSizeConfiguration = {
  particleStartSize: number;
  particleEndSize: number;
};

export type ParticleOpacityConfiguration = {
  particleStartOpacity: number;
  particleEndOpacity: number;
};

export type PerlinEffectConfiguration = CommonEffectConfiguration & {
  speed: number;
  noise: number;
  amplitude: number;
};

export type SwarmEffectConfiguration = {
  startPosition: StartPosition;
  animationType: AnimationType;
  speed: number;
  turbulence: number;
  radius: number;
} & CommonEffectConfiguration;

export type RevealEffectConfiguration = {
  windDirection: number;
  animationDirection: number;
  particleDuration: number;
  windSpeed: number;
  turbulence: number;
  animationType: AnimationType;
  radius: number;
} & CommonEffectConfiguration &
  ParticleColorConfiguration &
  ParticleSizeConfiguration &
  ParticleOpacityConfiguration;

export type EffectType = 'perlin' | 'swarm' | 'reveal';

export type EffectConfiguration =
  | PerlinEffectConfiguration
  | SwarmEffectConfiguration
  | RevealEffectConfiguration;

export type EffectToConfigurationMap = {
  perlin: PerlinEffectConfiguration;
  swarm: SwarmEffectConfiguration;
  reveal: RevealEffectConfiguration;
};

export type MovementFunction = ({
  particle,
  animationStartTime,
  requestAnimationFrameTime,
  canvasDimensions,
  animationDuration,
  textBoundaries,
}: {
  particle: Particle & { [key: string]: any };
  animationStartTime: number;
  requestAnimationFrameTime: number;
  canvasDimensions: Dimensions;
  animationDuration: number;
  textBoundaries: TextBoundaries;
}) => Particle & { [key: string]: any };

export type EffectMovement<T extends EffectType> = {
  factory: (config: EffectToConfigurationMap[T]) => MovementFunction;
};

export type PerlinPresetSkinParams = {
  [K in keyof Pick<
    typeof textParticlesSkinParams,
    'effect-type' | 'preset-id' | 'speed' | 'noise' | 'amplitude'
  >]: string;
};

export type SwarmPresetSkinParams = {
  [K in keyof Pick<
    typeof textParticlesSkinParams,
    | 'effect-type'
    | 'preset-id'
    | 'duration'
    | 'animation-type'
    | 'start-position'
    | 'speed'
    | 'turbulence'
  >]: string;
};
export type RevealPresetSkinParams = {
  [K in keyof Pick<
    typeof textParticlesSkinParams,
    | 'effect-type'
    | 'preset-id'
    | 'wind-direction'
    | 'animation-direction'
    | 'speed'
    | 'turbulence'
    | 'animation-type'
    | 'duration'
    | 'particle-start-size'
    | 'particle-end-size'
    | 'particle-start-opacity'
    | 'particle-end-opacity'
    | 'particle-duration'
  >]: string;
};
export type EffectPresetSkinParamsMap = {
  perlin: PerlinPresetSkinParams;
  swarm: SwarmPresetSkinParams;
  reveal: RevealPresetSkinParams;
};
